/**
 * @unscoped — i18n 공유 base — Cycle 41.
 *
 * admin-i18n.ts + drift-i18n.ts 의 공통 state (lang / storage / listener) 통합.
 * 두 모듈이 자체 lang 변수 / Set 유지하던 구조 → 단일 globalThis 객체 공유.
 *
 * 정책:
 *   - 단일 lang state (globalThis.__i18n) — admin/drift 가 항상 동일 값
 *   - 단일 storage key 'workchat:admin-lang' (cycle 38 의 admin-i18n 기준 유지 — 마이그레이션 비용 회피)
 *   - 단일 listener Set — setSharedLang 호출 시 양쪽 module 모두 통지
 *   - admin-i18n/drift-i18n 의 기존 public API (setAdminLang/setDriftLang) 는 thin wrapper 로 호환
 *
 * vitest ESM dual-instance 회피 — globalThis 기반 ensureState (cycle 39 패턴 일관).
 */

// Cycle 45 — third lang 'ja' (일본어) 추가.
export type Lang = 'ko' | 'en' | 'ja';

export const FALLBACK_LANG: Lang = 'ko';
export const SHARED_LANG_STORAGE_KEY = 'workchat:admin-lang';

export function isLang(v: unknown): v is Lang {
  return v === 'ko' || v === 'en' || v === 'ja';
}

interface I18nState { lang: Lang; listeners: Set<() => void> }
const G = globalThis as unknown as { __i18n?: I18nState };

function detectLangInitial(): Lang {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem(SHARED_LANG_STORAGE_KEY);
      if (isLang(stored)) return stored;
    } catch { /* private mode 무시 */ }
  }
  // 한국 대상 앱 — 명시 선택(localStorage)이 없으면 항상 한글 디폴트.
  // 이전엔 navigator.language 를 자동 감지해 영어/일본어 브라우저에서 로그인 화면부터
  // 그 언어로 표시됐다(한글 디폴트 위반). 영어/일본어는 설정 화면에서 명시 선택 시
  // localStorage 에 영속되고, 로그인 후에는 AuthGuard 가 서버 settings.language 를 적용한다.
  // navigator 기반 자동 감지가 필요한 경우 사용자가 AdminConsole 의 'auto' 옵션(setAutoLang)을 명시 선택.
  return FALLBACK_LANG; // 'ko'
}

function ensureState(): I18nState {
  if (!G.__i18n) {
    G.__i18n = { lang: detectLangInitial(), listeners: new Set() };
  }
  return G.__i18n!;
}

export function getSharedLang(): Lang {
  return ensureState().lang;
}

/**
 * Cycle 42 — lang → Intl locale BCP 47 tag.
 *   ko → ko-KR, en → en-US.
 *
 * date/number 형식 (toLocaleString, Intl.DateTimeFormat 등) 의 인자로 사용.
 * 향후 lang 추가 (ja → ja-JP, zh → zh-CN) 시 본 helper 단일 진입점.
 */
export function getDateLocale(): string {
  // Cycle 45 — ja 추가
  switch (getSharedLang()) {
    case 'en': return 'en-US';
    case 'ja': return 'ja-JP';
    default:   return 'ko-KR';
  }
}

/**
 * Cycle 43 — Number.toLocaleString 의 lang-aware locale.
 *   현재는 getDateLocale 와 동일 (ko-KR/en-US) — 동일 region 가정.
 *   향후 number-only locale 차이 발생 시 분리 (예: ko-KR-x-thousand) — 본 helper 가 분기 진입점.
 */
export function getNumberLocale(): string {
  return getDateLocale();
}

/**
 * Cycle 45 — Intl.NumberFormat currency formatter.
 *   lang-aware locale + currency code 결합. 기본 KRW (한국 원화).
 *   ko-KR + KRW → '₩1,234', en-US + KRW → '₩1,234' / 'KRW 1,234' (browser 마다 다름), ja-JP + KRW → '₩1,234'
 *
 *   결과 caching 없음 — locale/currency 가 적은 caller 라 매번 새 Intl.NumberFormat 생성 OK.
 *   향후 hot path 시 cache 검토.
 */
export function getCurrencyFormatter(currency: string = 'KRW'): Intl.NumberFormat {
  return new Intl.NumberFormat(getDateLocale(), { style: 'currency', currency });
}

/**
 * Cycle 54 — lang-aware 날짜/시각 포맷 SSOT.
 *
 * 배경: 컴포넌트 ~20곳이 `new Date(iso).toLocaleString(...)` 을 inline 처리하며 로케일이
 *   불일치 — 일부 getDateLocale(), 일부 하드코딩 'ko-KR', 일부 로케일 없는 bare default.
 *   → ja/en 사용자가 날짜를 한국어/브라우저기본으로 보는 i18n 버그 (cycle 45-47 ja 롤아웃 사각지대).
 *   추가로 invalid/null 입력 시 "Invalid Date" 노출 (NaN 가드 불일치).
 *
 * 본 SSOT 가 getDateLocale() 강제 + invalid/null → fallback 통일.
 *   - formatDate: 날짜만 (toLocaleDateString)
 *   - formatDateTime: 날짜+시각 (toLocaleString)
 *   - formatTime: 시각만 2-digit (toLocaleTimeString, hour/minute)
 */
function _parseDate(iso: string | number | Date | null | undefined): Date | null {
  if (iso == null || iso === '') return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string | number | Date | null | undefined, fallback = ''): string {
  const d = _parseDate(iso);
  return d == null ? fallback : d.toLocaleDateString(getDateLocale());
}

export function formatDateTime(iso: string | number | Date | null | undefined, fallback = ''): string {
  const d = _parseDate(iso);
  return d == null ? fallback : d.toLocaleString(getDateLocale());
}

export function formatTime(iso: string | number | Date | null | undefined, fallback = ''): string {
  const d = _parseDate(iso);
  return d == null ? fallback : d.toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit' });
}

/**
 * Cycle 44 — 사용자가 'auto' 선택 시 호출.
 *   localStorage 의 lang 명시 선택 제거 → 다음 detectLang 호출 시 navigator.language fallback.
 *   현재 lang state 도 즉시 navigator-detect 결과로 update + listener 통지.
 */
export function setAutoLang(): void {
  if (typeof localStorage !== 'undefined') {
    try { localStorage.removeItem(SHARED_LANG_STORAGE_KEY); } catch { /* */ }
  }
  // navigator detect — localStorage 빈 상태에서 detectLangInitial 동등 동작
  let detected: Lang = FALLBACK_LANG;
  if (typeof navigator !== 'undefined') {
    const raw = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
    // Cycle 45 — ja 추가 (detectLangInitial 와 일관)
    if (raw.startsWith('ja')) detected = 'ja';
    else if (raw.startsWith('en')) detected = 'en';
  }
  const s = ensureState();
  s.lang = detected;
  // idempotent — same-value 도 listener 통지 (cycle 41 정책 일관)
  for (const l of s.listeners) l();
}

/**
 * Cycle 44 — 사용자가 명시 lang 선택했는지 (localStorage 에 저장 있음).
 *   false → 'auto' 모드 (navigator detect).
 */
export function hasExplicitLang(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return isLang(localStorage.getItem(SHARED_LANG_STORAGE_KEY));
  } catch { return false; }
}

/**
 * 단일 진입점 — admin/drift 양쪽 모두 동일 lang 으로 통일 + listeners 통지.
 * - localStorage 영속 (write 실패는 silent — private mode 호환)
 * - 같은 값이어도 listener 통지 (cycle 39 의 cross-module early-return trap 회피 patch)
 */
export function setSharedLang(value: Lang): void {
  if (!isLang(value)) return;
  const s = ensureState();
  const changed = s.lang !== value;
  if (changed) {
    s.lang = value;
    if (typeof localStorage !== 'undefined') {
      try { localStorage.setItem(SHARED_LANG_STORAGE_KEY, value); } catch { /* */ }
    }
  }
  // 통지 — admin/drift 양쪽 React component 모두 re-render.
  // 같은 값이어도 통지 (idempotent — listener 가 결과 갱신 보장)
  for (const l of s.listeners) l();
}

export function subscribeSharedLang(fn: () => void): () => void {
  const s = ensureState();
  s.listeners.add(fn);
  return () => { s.listeners.delete(fn); };
}

/**
 * 서버 settings.language 적용 — 부트(로그인 직후) sync 전용.
 *
 * setSharedLang 과 달리 localStorage 에 저장하지 않는다: 서버 settings 가 사용자 계정 언어의
 * SSOT 이므로 부트(AuthGuard probe 직후)마다 서버값을 i18n state 에만 반영한다 — 다른 기기에서
 * 변경해도 이 기기 부트에 즉시 반영되고, stale localStorage 값에 가려지지 않는다.
 *
 * 봉합: 기존엔 부트 시 서버 settings.language 를 i18n 에 잇는 코드가 없어, localStorage 미설정
 * 사용자는 navigator.language(브라우저 언어)로 fallback — 설정이 한국어여도 영어 브라우저면
 * 전체 UI 가 영어로 표시됐다.
 */
export function applyServerLang(value: Lang): void {
  if (!isLang(value)) return;
  const s = ensureState();
  if (s.lang === value) return;
  s.lang = value;
  for (const l of s.listeners) l();
}

/** test 격리용 — production 호출 금지. */
export function _setSharedLangForTests(value: Lang): void {
  const s = ensureState();
  s.lang = value;
  for (const l of s.listeners) l();
}

/** test 진단용. */
export function _getSharedLangForTests(): Lang {
  return ensureState().lang;
}
